/**
 * The `profile` namespace, in English — the person's own screen.
 *
 * Its own namespace rather than more of `app`, per the rule in `lib/i18n/catalog.ts`: `app`
 * is the chrome every screen family reads, and this is a screen family of its own — the one
 * that will grow the name, the sessions list and the notification preferences.
 *
 * **The language sentence names the studio, because the studio is what decides it**
 * (`MCK-20260521-010`, owner 2026-09-12). It exists precisely because the behaviour is no
 * longer self-evident: a person who changed the language on the sign-in screen and then sees
 * something else needs to be told who overruled them and where that decision is made. See
 * `features/profile/ProfilePage`.
 *
 * **The password copy names the consequences before the act and again after it**
 * (`KLQ-20260821-002` §C). Every branch signs the person out of every other browser and sends
 * them mail, and a person who learns that from their phone an hour later concludes something
 * broke. Two variants of each sentence, because setting a first password and replacing one
 * are not the same event: the first is a second way in, the second is a replacement.
 */

export const profileEn = {
  'page.title': 'Your profile',
  'page.subtitle': 'The settings that follow you rather than your company.',

  'language.title': 'Language',
  'language.label': 'Interface language',
  'language.studio':
    'Kalqa is in {language} because your studio chose it. Everyone in the studio reads the same language, whatever their browser is set to.',
  'language.studio.where': 'A studio admin can change it in Studio settings.',

  'dateFormat.title': 'Date format',
  'dateFormat.label': 'Show dates as',
  'dateFormat.option.auto': 'Automatic ({example})',
  'dateFormat.option.numeric': 'Numeric ({example})',
  'dateFormat.option.medium': 'Long ({example})',
  'dateFormat.hint': 'Remembered in this browser only. The language stays as it is.',

  'password.title': 'Password',
  'password.intro.change':
    'This browser stays signed in. Every other one is signed out straight away, and we email you about the change either way.',
  'password.intro.set':
    'You have been signing in without a password. Choosing one gives you a second way in; the way you use today keeps working. Every other browser is signed out straight away.',

  'password.field.current.label': 'Current password',
  'password.field.new.label': 'New password',
  'password.field.new.hint': 'At least {min} characters. Length is the only rule.',
  'password.field.confirm.label': 'New password again',
  'password.field.show': 'Show password',
  'password.field.hide': 'Hide password',

  'password.action.change': 'Change password',
  'password.action.set': 'Set password',

  'password.done.change':
    'Password changed. Your other browsers have been signed out and an email is on its way.',
  'password.done.set':
    'Password set. Your other browsers have been signed out and an email is on its way.',

  'password.error.currentRequired': 'Enter your current password.',
  'password.error.wrongCurrent': 'That is not your current password.',
  'password.error.tooShort': 'Use at least {min} characters.',
  'password.error.tooLong': 'Use {max} characters or fewer.',
  'password.error.mismatch': 'The two new passwords do not match.',
  'password.error.rateLimited': 'Too many attempts. Try again in a few minutes.',
  'password.error.rateLimitedIn':
    'Too many attempts. Try again in {minutes, plural, one {# minute} other {# minutes}}.',
  'password.error.gone': 'This account is no longer active. Sign in again.',
  'password.error.unexpected': 'Something went wrong on our side. Your password was not changed.',

  'missing.rest':
    'Your name and address are not editable here yet. Nothing else on this screen is built.',
} as const;

export type ProfileMessageKey = keyof typeof profileEn;
